/**
 * FERP GPS Punch Hooks
 *
 * Session 3: Rewired from ERP → Payroll subgraph.
 *
 * GPS data is now FIRST-CLASS on PayrollPunchRecord (lat/lng/accuracy fields)
 * instead of the JSON-in-comments hack. The payrollClockIn mutation creates
 * both a PayrollTimeEntry AND an immutable PayrollPunchRecord with GPS data.
 * The Core Event for GPS audit trail is still created fire-and-forget.
 *
 * Architecture:
 *   GPS Punch In  = payrollClockIn(input with GPS fields) + createEvent(GPS_CLOCK_IN)
 *   GPS Punch Out = payrollClockOut(input with GPS fields) + createEvent(GPS_CLOCK_OUT)
 */
// @ts-nocheck

import { useCompatQuery, useCompatMutation } from './useApolloCompat';
import {
  ENHANCED_PUNCHES_QUERY,
  GPS_PUNCH_IN_MUTATION,
  GPS_PUNCH_OUT_MUTATION,
  GPS_EVENT_MUTATION,
  PUNCH_POLICY_QUERY,
  UPDATE_PUNCH_POLICY_MUTATION,
  OVERRIDE_PUNCH_MUTATION,
} from '../graphql/operations';

// ── Payroll PunchRecord → Frontend transform ────────────────────────────

function punchRecordToEnhancedPunch(punch: any) {
  return {
    id: punch.id,
    employeeId: punch.employeeId || '',
    employeeName: punch.employeeId || '',
    type: punch.punchType === 'CLOCK_OUT' ? 'OUT' : 'IN',
    timestamp: punch.timestamp || punch.createdAt || '',
    gps: punch.latitude != null ? {
      latitude: punch.latitude,
      longitude: punch.longitude,
      accuracy: punch.accuracy,
      altitude: null,
      timestamp: punch.timestamp,
      insideFence: true,
      deviceInfo: punch.deviceInfo,
    } : undefined,
    biometric: undefined,
    source: punch.source || 'WEB',
    note: punch.notes,
    status: 'VALID',
    timeEntryId: punch.timeEntryId,
  };
}

// ── Enhanced Punches Query (Payroll PunchRecords) ───────────────────────

export function useEnhancedPunches(variables?: {
  employeeId?: string;
  startDate?: string;
  endDate?: string;
  status?: string;
}) {
  const result = useCompatQuery(ENHANCED_PUNCHES_QUERY, {
    variables: { employeeId: variables?.employeeId, limit: 200 },
  });

  const punches = (result.data?.payrollPunchRecords || [])
    .map(punchRecordToEnhancedPunch)
    .filter((p: any) => {
      if (variables?.employeeId && p.employeeId !== variables.employeeId) return false;
      return true;
    });

  return {
    data: punches,
    loading: result.loading,
    isLoading: result.loading,
    error: result.error,
    refetch: result.refetch,
  };
}

// ── GPS Punch In (Payroll ClockIn + Core Event) ─────────────────────────

/**
 * Returns compat interface (.mutate, .isPending) for page compatibility.
 * GPS data goes to PayrollPunchRecord as first-class fields (lat/lng/accuracy).
 * Core Event is still fire-and-forget for immutable GPS audit trail.
 *
 * Geofence validation: Clock-in requires either:
 * 1. Work device (FERP Hub, managed tablet) — detected via device fingerprint
 * 2. GPS location within facility geofence
 */
export function useGpsPunchIn() {
  const mutation = useCompatMutation(GPS_PUNCH_IN_MUTATION, {
    refetchQueries: [{ query: ENHANCED_PUNCHES_QUERY, variables: { limit: 200 } }],
  });

  const eventMutation = useCompatMutation(GPS_EVENT_MUTATION);

  const originalMutate = mutation.mutate;
  mutation.mutate = (variables?: any, callbacks?: any) => {
    const now = new Date().toISOString();

    // Check for work device (FERP Hub or registered device)
    const deviceFingerprint = localStorage.getItem('ferp_device_fingerprint');
    const isWorkDevice = localStorage.getItem('ferp_is_work_device') === 'true';

    // GPS data is now first-class input fields on payrollClockIn
    // Geofence validation happens on backend: work device OR within geofence
    const input = {
      employeeId: variables?.employeeId || '',
      source: variables?.source || 'MOBILE',
      latitude: variables?.latitude,
      longitude: variables?.longitude,
      accuracy: variables?.accuracy,
      deviceInfo: variables?.deviceInfo,
      note: variables?.note,
      isWorkDevice: isWorkDevice || variables?.isWorkDevice || false,
      deviceFingerprint: deviceFingerprint || variables?.deviceFingerprint,
    };

    originalMutate({ input }, {
      onSuccess: (data: any) => {
        const timeEntry = data?.payrollClockIn;

        // Fire-and-forget: create immutable GPS event in core (audit trail)
        if (timeEntry) {
          eventMutation.mutate({
            tenantId: timeEntry.tenantId,
            input: {
              eventName: 'GPS_CLOCK_IN',
              eventType: 'TIME_ATTENDANCE',
              description: `GPS punch-in: ${variables?.latitude},${variables?.longitude} (accuracy: ${variables?.accuracy}m)`,
              metadata: JSON.stringify({
                timeEntryId: timeEntry.id,
                latitude: variables?.latitude,
                longitude: variables?.longitude,
                accuracy: variables?.accuracy,
                source: variables?.source || 'MOBILE',
                timestamp: now,
              }),
            },
          });
        }

        if (callbacks?.onSuccess) {
          callbacks.onSuccess(timeEntry ? punchRecordToEnhancedPunch({
            ...timeEntry,
            punchType: 'CLOCK_IN',
            timestamp: timeEntry.startTime || now,
            latitude: variables?.latitude,
            longitude: variables?.longitude,
            accuracy: variables?.accuracy,
          }) : data);
        }
      },
      onError: callbacks?.onError,
    });
  };

  return mutation;
}

// ── GPS Punch Out (Payroll ClockOut + Core Event) ────────────────────────

/**
 * Returns compat interface (.mutate, .isPending) for page compatibility.
 * Transitions PayrollTimeEntry (clocked_in → clocked_out) with GPS fields.
 */
export function useGpsPunchOut() {
  const mutation = useCompatMutation(GPS_PUNCH_OUT_MUTATION, {
    refetchQueries: [{ query: ENHANCED_PUNCHES_QUERY, variables: { limit: 200 } }],
  });

  const eventMutation = useCompatMutation(GPS_EVENT_MUTATION);

  const originalMutate = mutation.mutate;
  mutation.mutate = (variables?: any, callbacks?: any) => {
    const now = new Date().toISOString();

    // GPS fields passed directly (no JSON-in-comments hack)
    const input = {
      timeEntryId: variables?.timeEntryId || '',
      note: variables?.note,
      latitude: variables?.latitude,
      longitude: variables?.longitude,
      accuracy: variables?.accuracy,
    };

    originalMutate({ input }, {
      onSuccess: (data: any) => {
        const timeEntry = data?.payrollClockOut;

        // Fire-and-forget: create immutable GPS event in core (audit trail)
        if (timeEntry) {
          eventMutation.mutate({
            tenantId: timeEntry.tenantId,
            input: {
              eventName: 'GPS_CLOCK_OUT',
              eventType: 'TIME_ATTENDANCE',
              description: `GPS punch-out: ${variables?.latitude},${variables?.longitude} (accuracy: ${variables?.accuracy}m)`,
              metadata: JSON.stringify({
                timeEntryId: variables?.timeEntryId,
                employeeId: timeEntry.employeeId,
                latitude: variables?.latitude,
                longitude: variables?.longitude,
                accuracy: variables?.accuracy,
                source: variables?.source || 'MOBILE',
                timestamp: now,
              }),
            },
          });
        }

        if (callbacks?.onSuccess) {
          callbacks.onSuccess(timeEntry ? punchRecordToEnhancedPunch({
            ...timeEntry,
            punchType: 'CLOCK_OUT',
            timestamp: timeEntry.endTime || now,
            latitude: variables?.latitude,
            longitude: variables?.longitude,
            accuracy: variables?.accuracy,
          }) : data);
        }
      },
      onError: callbacks?.onError,
    });
  };

  return mutation;
}

// ── Policy Hooks (unchanged — use core primitives) ──────────────────────

export function usePunchPolicy() {
  return useCompatQuery(PUNCH_POLICY_QUERY);
}

export function useUpdatePunchPolicy() {
  return useCompatMutation(UPDATE_PUNCH_POLICY_MUTATION, {
    refetchQueries: [{ query: PUNCH_POLICY_QUERY }],
  });
}

export function useOverridePunch() {
  return useCompatMutation(OVERRIDE_PUNCH_MUTATION, {
    refetchQueries: [{ query: ENHANCED_PUNCHES_QUERY }],
  });
}

// ── GPS Utility ─────────────────────────────────────────────────────────

/**
 * Hook to get the current GPS location from the browser.
 * Returns a promise-based helper for imperative use.
 */
export function useCurrentLocation() {
  const getCurrentPosition = (): Promise<GeolocationPosition> =>
    new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error('Geolocation not supported'));
        return;
      }
      navigator.geolocation.getCurrentPosition(resolve, reject, {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0,
      });
    });

  return { getCurrentPosition };
}
